import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "w-full h-full xl:pt-[65px] bg-secondary flex overflow-auto" }
const _hoisted_2 = { class: "h-full w-full flex items-center justify-center" }

import {
  ref,
  reactive,
  onMounted,
  onBeforeUnmount,
  Ref,
  computed,
  watch,
} from "vue";
import Konva from "konva";
import domtoimage from "dom-to-image";
import ActionsComponent from "@/views/Calculators/ApartmentPlan/components/ActionsComponent.vue";
import { ImageNodeConfig, Point } from "@/types/stage";
import StageComponent from "@/views/Calculators/ApartmentPlan/components/StageComponent.vue";
import NoImageComponent from "@/views/Calculators/ApartmentPlan/components/NoImageComponent.vue";
import { useVueToPrint } from "vue-to-print";
import { exportElementAsPDF } from "@/utils/exports";
import {
  APARTMENT_MONTHS_VALUES,
  IApartmentMonth,
  MonthValue,
  ToggleKey,
} from "@/constants/apartmentPlan";
import { fetchAptDayParams } from "@/api/apartmentPlan";
import { AptDateParamsResponse } from "@/types/apartmentPlan";

// NEW STATE
import Circle = Konva.Circle;
import Stage = Konva.Stage;

export default /*@__PURE__*/_defineComponent({
  __name: 'ApartmentPlanView',
  setup(__props) {

const points = ref<Point[]>([]);
const stageRef = ref<any>(null);
const selectedImageRef = ref<File | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
// Points Actions
const addPointsMode = ref(false);
const movePointsMode = ref(false);
const removePointsMode = ref(false);
// Facade
const azimuthFacade = ref<number>(0);
const buildYear = ref<number | null>(null);
const starsYear = ref<number | null>(null);
const buildMonth = ref<MonthValue>(APARTMENT_MONTHS_VALUES.january);
const diagramDateParams = ref<AptDateParamsResponse>();
const facadeDirection = ref<number | null>(null);

const toggles = reactive<Record<ToggleKey, boolean>>({
  "lo-shoo": false,
  "sectors-diagram": false,
  "tai-shoo": false,
  "sui-po": false,
  "year-sha": false,
  "month-sha": false,
  "noble-stars": false,
  "middle-12-mountains": false,
  "border-12-mountains": false,
  "move-mountains-labels": false,
  "current-stars": false,
});
const diagramToggles: ToggleKey[] = [
  "tai-shoo",
  "sui-po",
  "year-sha",
  "month-sha",
  "noble-stars",
  "current-stars",
];

const dateParams = computed(() => {
  if (starsYear.value === null) {
    return null;
  }

  return `${starsYear.value}-${buildMonth.value.month}-${buildMonth.value.day}`;
});

watch(dateParams, async (newDate: string | null) => {
  diagramDateParams.value = undefined;
  if (newDate !== null) {
    diagramDateParams.value = await fetchAptDayParams(newDate);
  }
});

function handleFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (!input.files || input.files.length === 0) return;

  const file = input.files[0];
  selectedImageRef.value = file;
  // Update Image for Konva
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    image.value = img;
    imageNode.image = img;
    imageNode.x = stageWidth.value / 2;
    imageNode.y = stageHeight.value / 2;
    imageNode.offsetX = img.width / 2;
    imageNode.offsetY = img.height / 2;
  };
  img.src = url;
}

function handleClearImage() {
  selectedImageRef.value = null;
  imageNode.image = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
  points.value = [];
}

function handleImageScale(scale: number) {
  imageNode.scaleX = scale;
  imageNode.scaleY = scale;
}
// END NEW STATE

// Drawable Container Params
const stageWidth = ref<number>(0);
const stageHeight = ref<number>(0);
const diagramScale = ref<number>(1);
const drawableContainerRef = ref<HTMLElement | null>(null);
const image = ref<HTMLImageElement | null>(null);
const imageNode = reactive<ImageNodeConfig>({
  x: 0,
  y: 0,
  offsetX: 0,
  offsetY: 0,
  scaleX: 1,
  scaleY: 1,
  draggable: false,
  image: null as HTMLImageElement | null,
});

const { handlePrint } = useVueToPrint({
  content: stageRef,
  removeAfterPrint: true,
});

function updateStageSize() {
  const el = drawableContainerRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const ratio = Math.sqrt(2); // A4 ratio
  let finalWidth = rect.width;
  let finalHeight = Math.round(rect.width / ratio);
  if (finalHeight > rect.height) {
    finalHeight = rect.height;
    finalWidth = Math.round(rect.height * ratio);
  }
  stageWidth.value = finalWidth;
  stageHeight.value = finalHeight;
}

onMounted(() => {
  updateStageSize();
  window.addEventListener("resize", updateStageSize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateStageSize);
});

function addPoint(e: Stage) {
  if (!addPointsMode.value) return;
  const pos = e.target.getStage().getPointerPosition();
  if (pos) points.value.push({ x: pos.x, y: pos.y });
}

function onDragPoint(index: number, e: Circle) {
  points.value[index].x = e.target.x();
  points.value[index].y = e.target.y();
}

function removePoint(index: number, e: Circle) {
  if (!removePointsMode.value) return;
  e.evt.preventDefault();
  points.value.splice(index, 1);
}

function exportStageAsImage() {
  const el = drawableContainerRef.value;
  if (!el) return;

  domtoimage
    .toPng(el, {
      quality: 1,
      width: el.scrollWidth,
      height: el.scrollHeight,
    })
    .then((dataUrl) => {
      const link = document.createElement("a");
      link.download = "apt-planing.png";
      link.href = dataUrl;
      link.click();
    });
}

function exportStageAsPDF() {
  if (drawableContainerRef.value) {
    exportElementAsPDF(drawableContainerRef.value);
  }
}

function handleTogglePointsMode(
  modeName: "addPoint" | "removePoint" | "movePoint"
) {
  const modes: Record<string, Ref<boolean>> = {
    addPoint: addPointsMode,
    removePoint: removePointsMode,
    movePoint: movePointsMode,
  };
  Object.entries(modes).forEach(([modeKey, modeValue]) => {
    if (modeKey !== modeName) {
      modeValue.value = false;
    } else {
      modeValue.value = !modeValue.value;
    }
  });
}

const hangleToggle = (key: ToggleKey) => {
  toggles[key] = !toggles[key];

  if (diagramToggles.includes(key) && toggles[key]) {
    toggles["sectors-diagram"] = true;
  } else if (key === "sectors-diagram" && !toggles[key]) {
    diagramToggles.forEach((key: ToggleKey) => {
      toggles[key] = false;
    });
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "relative flex-1 min-w-[600px] overflow-hidden",
      ref_key: "drawableContainerRef",
      ref: drawableContainerRef
    }, [
      _createElementVNode("div", _hoisted_2, [
        (selectedImageRef.value)
          ? (_openBlock(), _createBlock(StageComponent, {
              key: 0,
              ref_key: "stageRef",
              ref: stageRef,
              "stage-width": stageWidth.value,
              "stage-height": stageHeight.value,
              points: points.value,
              "image-node": imageNode,
              "diagram-scale": diagramScale.value,
              "diagram-date-params": diagramDateParams.value,
              "move-points-mode": movePointsMode.value,
              facadeDirection: facadeDirection.value,
              buildYear: buildYear.value,
              "azm-facade": azimuthFacade.value,
              toggles: toggles,
              "onPoint:add": addPoint,
              "onPoint:move": onDragPoint,
              "onPoint:remove": removePoint
            }, null, 8, ["stage-width", "stage-height", "points", "image-node", "diagram-scale", "diagram-date-params", "move-points-mode", "facadeDirection", "buildYear", "azm-facade", "toggles"]))
          : (_openBlock(), _createBlock(NoImageComponent, {
              key: 1,
              "onImage:select": _cache[0] || (_cache[0] = ($event: any) => (fileInputRef.value?.click()))
            }))
      ])
    ], 512),
    _createVNode(ActionsComponent, {
      toggles: toggles,
      hasDiagramDate: diagramDateParams.value !== undefined,
      "image-name": selectedImageRef.value?.name ?? null,
      onSelectImage: _cache[1] || (_cache[1] = ($event: any) => (fileInputRef.value?.click())),
      onClearImage: handleClearImage,
      scale: imageNode.scaleX,
      "diagram-scale": diagramScale.value,
      facadeDirection: facadeDirection.value,
      addPointMode: addPointsMode.value,
      movePointMode: movePointsMode.value,
      removePointMode: removePointsMode.value,
      "onUpdate:buildYear": _cache[2] || (_cache[2] = (newBuildYear) => (buildYear.value = newBuildYear)),
      "onUpdate:starsYear": _cache[3] || (_cache[3] = (newStarsYear) => (starsYear.value = newStarsYear)),
      "onUpdate:buildMonth": _cache[4] || (_cache[4] = (newBuildMonth) => (buildMonth.value = newBuildMonth)),
      onChangeFacadeDirection: _cache[5] || (_cache[5] = (directionDeg) => (facadeDirection.value = directionDeg)),
      "onUpdate:scale": handleImageScale,
      "onUpdate:diagramScale": _cache[6] || (_cache[6] = (scale) => diagramScale.value = scale),
      azm: azimuthFacade.value,
      "onUpdate:azm": _cache[7] || (_cache[7] = ($event: any) => ((azimuthFacade).value = $event)),
      "onToggle:moveImage": _cache[8] || (_cache[8] = ($event: any) => (imageNode.draggable = !imageNode.draggable)),
      "onToggle:addPoint": _cache[9] || (_cache[9] = ($event: any) => (handleTogglePointsMode('addPoint'))),
      "onToggle:movePoint": _cache[10] || (_cache[10] = ($event: any) => (handleTogglePointsMode('movePoint'))),
      "onToggle:removePoint": _cache[11] || (_cache[11] = ($event: any) => (handleTogglePointsMode('removePoint'))),
      onToggle: hangleToggle,
      onExportAsPdf: exportStageAsPDF,
      onExportAsPng: exportStageAsImage,
      onPrint: _unref(handlePrint)
    }, null, 8, ["toggles", "hasDiagramDate", "image-name", "scale", "diagram-scale", "facadeDirection", "addPointMode", "movePointMode", "removePointMode", "azm", "onPrint"]),
    _createElementVNode("input", {
      ref_key: "fileInputRef",
      ref: fileInputRef,
      type: "file",
      accept: "image/*",
      class: "hidden",
      onChange: handleFileChange
    }, null, 544)
  ]))
}
}

})